import { useState } from 'react';
import { eComponentType, eFormatType } from 'src/pages/WhatsappApi/WhatsappApi.enum.ts';
import { tCard, tComponent } from 'src/pages/WhatsappApi/WhatsappApi.types.ts';
import { tTemplateCarouselPreviewProps } from './TemplateCarouselPreview.types.ts';
import { formatWhatsappText } from './TemplateCarouselPreview.functions.tsx';
import './TemplateCarouselPreview.styles.scss';

function getComponentByType(
  components: tComponent[] | undefined,
  type: eComponentType,
) {
  return components?.find((c) => c.type === type);
}

function CarouselCard(props: { card: tCard }) {
  // INT-1302 — the sent-template bubble in the chat now renders this preview
  // over URLs frozen into a chat event, so a dead or expired media URL would
  // turn today's clean text bubble into a row of broken-image glyphs: strictly
  // worse than the bug being fixed. Falling back to the same empty
  // __CardMedia placeholder a card without media already shows keeps the worst
  // case at "no image" instead of "broken image".
  // Keyed by the URL that failed, not a boolean: this preview is also the
  // wizard's live preview, where the operator can swap the card media. A
  // boolean would leave a card blank forever after one bad URL, even once it
  // is fixed — the failed URL self-resets as soon as a different one arrives.
  const [failedMediaUrl, setFailedMediaUrl] = useState<string | null>(null);
  const header = getComponentByType(props.card.components, eComponentType.HEADER);
  const body = getComponentByType(props.card.components, eComponentType.BODY);
  const buttons = getComponentByType(props.card.components, eComponentType.BUTTONS);
  const mediaUrl = header?.example?.header_handle?.[0];

  // Declared format wins; the extension is only a fallback. The order is
  // load-bearing (INT-1279): re-hosted card handles are signed URLs with no
  // extension, so sniffing first would drop a VIDEO card to <img> on every
  // Meta-synced surface this preview is shared by. Only the wizard's live
  // preview lacks a format, and there the extension fallback catches it.
  const isVideo =
    header?.format === eFormatType.VIDEO ||
    /\.(mp4|mov|webm)($|\?)/i.test(mediaUrl ?? '');

  return (
    <div className="TemplateCarouselPreview__Card">
      {mediaUrl && failedMediaUrl !== mediaUrl ? (
        isVideo ? (
          <video
            src={mediaUrl}
            muted
            playsInline
            // No controls and no poster, so without this the browser paints a
            // black rectangle until something forces a frame to decode.
            preload="metadata"
            onError={() => setFailedMediaUrl(mediaUrl)}
            className="TemplateCarouselPreview__CardMedia object-cover"
          />
        ) : (
          <img
            src={mediaUrl}
            alt=""
            onError={() => setFailedMediaUrl(mediaUrl)}
            className="TemplateCarouselPreview__CardMedia object-cover"
          />
        )
      ) : (
        <div className="TemplateCarouselPreview__CardMedia" />
      )}

      {body?.text ? (
        <p className="text-xs text-base-content p-1.5 break-words whitespace-pre-wrap">
          {formatWhatsappText(body.text, 'TemplateCarouselPreview__Variable')}
        </p>
      ) : null}

      {buttons?.buttons && buttons.buttons.length > 0 ? (
        <div className="TemplateCarouselPreview__ButtonList">
          {buttons.buttons.map((btn, idx) => (
            <div key={idx} className="TemplateCarouselPreview__ButtonItem">
              {btn.text}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Read-only horizontal strip of carousel cards for a WhatsApp API template
 * (INT-1276). Renders nothing when the template carries no CAROUSEL component
 * or the carousel has no cards, so it can be mounted unconditionally on any
 * template preview surface.
 */
export default function TemplateCarouselPreview(
  props: tTemplateCarouselPreviewProps,
) {
  const carousel = getComponentByType(props.components, eComponentType.CAROUSEL);

  if (!carousel?.cards || carousel.cards.length === 0) return null;

  return (
    <div
      className="TemplateCarouselPreview"
      data-testid="template-carousel-preview"
    >
      {carousel.cards.map((card, idx) => (
        <CarouselCard key={idx} card={card} />
      ))}
    </div>
  );
}
